import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Header, Icon, Label, Message, Segment, Table } from 'semantic-ui-react';
import { MONTH_NAMES, monthOfAsAt } from '../periods.js';
import { fmt, latestPerReport } from '../summary.js';
import { loadValidation, validationLabel } from '../store/validationStore.js';
import { STATUS, buildValidation, filterComparison, lastNewMonth, signed, tally, yearsOf } from '../validation.js';

const NO_FILTER = { partner: '', reportId: '', month: 'all', line: '', show: 'differences', scope: 'all' };
const PAGE = 200;
const show = (r, v) => (r.kind === 'text' ? (v ?? '') : fmt(r.kind, v)) || '–';
const BADGE = { match: ['check circle', 'green'], differs: ['exclamation triangle', 'orange'], missing: ['question circle', 'red'], new: ['plus circle', 'blue'] };

/* Compares two sets of values: "Reported + new data" (what each report says, plus the new data for months it does not cover yet)
 * against "All new data" (the _latest tables), so you can see how the new data compares with what was reported.
 * Header fields (settlement_report_latest) and monthly line items (settlement_report_figure_latest) are compared in the same table. */
export default function ValidationTab({ reports, loadFull }) {
  const [newRows, setNewRows] = useState(null);                    // settlement_report_figure_latest
  const [newReports, setNewReports] = useState(null);              // settlement_report_latest (null: none)
  const [loadError, setLoadError] = useState(null);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [filters, setFilters] = useState(NO_FILTER);
  const [limit, setLimit] = useState(PAGE);
  const [entries, setEntries] = useState(null);                    // [{ report, figures }] for the chosen year
  const [error, setError] = useState(null);
  const uid = useId();

  useEffect(() => {
    let live = true;
    loadValidation().then((d) => { if (live) { setNewRows(d.figures); setNewReports(d.reports); } }).catch((e) => { if (live) setLoadError(e.message); });
    return () => { live = false; };
  }, []);

  const years = useMemo(() => [...new Set([...reports.map((r) => r.report_year), ...yearsOf(newRows || []), ...(newReports || []).map((r) => Number(r.report_year))])].sort((a, b) => b - a), [reports, newRows, newReports]);
  const y = year ?? years[0] ?? null;
  const picked = useMemo(() => (y === null ? [] : latestPerReport(reports, y)), [reports, y]);
  const m = month ?? (newRows && y !== null ? lastNewMonth(newRows, y) : null) ?? (picked.length ? Math.max(...picked.map((r) => monthOfAsAt(r.as_at_date))) : 0);

  useEffect(() => {
    let live = true;
    setEntries(null); setError(null);
    Promise.all(picked.map((r) => loadFull(r))).then((all) => { if (live) setEntries(all); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [picked, loadFull]);

  const all = useMemo(() => (entries && newRows && y !== null ? buildValidation(entries, newRows, newReports, y, m) : null), [entries, newRows, newReports, y, m]);
  const only = filters.month !== 'all' && Number(filters.month) <= m ? filters.month : 'all';
  const shown = useMemo(() => (all ? filterComparison(all, { ...filters, month: only }) : []), [all, filters, only]);
  const counts = useMemo(() => tally(all || []), [all]);
  const partners = useMemo(() => [...new Set((all || []).map((r) => r.partner).filter(Boolean))].sort(), [all]);
  const ids = useMemo(() => [...new Set((all || []).map((r) => r.id))].sort(), [all]);
  const filtered = filters.scope !== 'all' || filters.partner.trim() || filters.reportId.trim() || filters.line.trim() || only !== 'all' || filters.show !== NO_FILTER.show;
  const set = (patch) => { setFilters((f) => ({ ...f, ...patch })); setLimit(PAGE); };
  const visible = shown.slice(0, limit);

  return (
    <section aria-labelledby="val-heading">
      <Header as="h2" id="val-heading" className="titlebar">
        <Icon name="balance scale" />
        <Header.Content>Validation<Header.Subheader>How the new data compares with what was reported: header fields and monthly line items</Header.Subheader></Header.Content>
      </Header>
      <div className="toolbar" role="group" aria-label="Choose the validation period">
        <label htmlFor="val-year">Year</label>
        <select id="val-year" className="ui dropdown" value={y ?? ''} onChange={(e) => { setYear(Number(e.target.value)); setMonth(null); }}>
          {years.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <label htmlFor="val-month">Month</label>
        <select id="val-month" className="ui dropdown" value={m} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
        </select>
        <span className="hint">January to the month chosen · new data from {newRows ? validationLabel() : '…'}</span>
      </div>
      <div className="toolbar filterbar" role="search" aria-label="Filter the validation">
        <span className="fgroup">
          <label htmlFor={`${uid}-partner`}>Partner</label>
          <input id={`${uid}-partner`} type="search" className="filterinput" list={`${uid}-partners`} placeholder="Any partner" autoComplete="off"
            value={filters.partner} onChange={(e) => set({ partner: e.target.value })} />
          <datalist id={`${uid}-partners`}>{partners.map((p) => <option key={p} value={p} />)}</datalist>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-report`}>Report ID</label>
          <input id={`${uid}-report`} type="search" className="filterinput short" list={`${uid}-ids`} placeholder="Any report" autoComplete="off"
            value={filters.reportId} onChange={(e) => set({ reportId: e.target.value })} />
          <datalist id={`${uid}-ids`}>{ids.map((p) => <option key={p} value={p} />)}</datalist>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-only`}>Only month</label>
          <select id={`${uid}-only`} className="ui dropdown" value={only} onChange={(e) => set({ month: e.target.value })}>
            <option value="all">All months</option>
            {MONTH_NAMES.slice(0, m + 1).map((n, i) => <option key={n} value={i}>{n}</option>)}
          </select>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-scope`}>Compare</label>
          <select id={`${uid}-scope`} className="ui dropdown" value={filters.scope} onChange={(e) => set({ scope: e.target.value })}>
            <option value="all">Header and line items</option>
            <option value="header">Header only</option>
            <option value="line">Line items only</option>
          </select>
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-line`}>Field / line item</label>
          <input id={`${uid}-line`} type="search" className="filterinput" placeholder="e.g. VAT, power, socket" autoComplete="off"
            value={filters.line} onChange={(e) => set({ line: e.target.value })} />
        </span>
        <span className="fgroup">
          <label htmlFor={`${uid}-show`}>Show</label>
          <select id={`${uid}-show`} className="ui dropdown" value={filters.show} onChange={(e) => set({ show: e.target.value })}>
            <option value="differences">Differences only</option>
            <option value="all">All rows</option>
            <option value="matches">Matching rows</option>
            <option value="new">New periods only</option>
          </select>
        </span>
        {filtered && <Button type="button" basic compact size="small" icon="close" labelPosition="left" content="Reset filters" onClick={() => { setFilters(NO_FILTER); setLimit(PAGE); }} />}
      </div>

      {(error || loadError) && <Message negative role="alert">{error || loadError}</Message>}
      {!error && !loadError && !all && y !== null && <Message info role="status">Loading reports and new data…</Message>}
      {y === null && <Segment placeholder><Header icon><Icon name="folder open outline" />There are no reports or new data yet.</Header></Segment>}
      {all && all.length === 0 && <Message info>No reported or new figures for {y} up to {MONTH_NAMES[m]}.</Message>}
      {all && all.length > 0 && (
        <>
          <dl className="stats" aria-label="Validation results">
            <div><dt>Reported values checked</dt><dd>{counts.compared}</dd></div>
            <div className="ok"><dt>Match</dt><dd>{counts.match}</dd></div>
            <div className={counts.differs ? 'warn' : 'ok'}><dt>Differ</dt><dd>{counts.differs}</dd></div>
            <div className={counts.missing ? 'bad' : 'ok'}><dt>Not in new data</dt><dd>{counts.missing}</dd></div>
            <div><dt>New periods</dt><dd>{counts.new}</dd></div>
          </dl>
          <p className="hint rowcount" role="status">Showing {Math.min(visible.length, shown.length)} of {shown.length} matching rows ({all.length} in total).</p>
          {shown.length === 0 ? <Message info>No rows match the filters.</Message> : (
            <div className="gridwrap" tabIndex={0} role="region" aria-label="Validation comparison (scrollable)">
              <Table celled compact unstackable className="datagrid valtable">
                <caption className="visually-hidden">Reported plus new data compared with all new data, one row per report and header field, or per report, month and line item.</caption>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell scope="col" className="lab c1">Report</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Month</Table.HeaderCell>
                    <Table.HeaderCell scope="col" className="lineitem">Field / line item</Table.HeaderCell>
                    <Table.HeaderCell scope="col" textAlign="right" className="set1">Reported + new data</Table.HeaderCell>
                    <Table.HeaderCell scope="col" textAlign="right" className="set2">All new data</Table.HeaderCell>
                    <Table.HeaderCell scope="col" textAlign="right">Difference</Table.HeaderCell>
                    <Table.HeaderCell scope="col" textAlign="right">%</Table.HeaderCell>
                    <Table.HeaderCell scope="col">Status</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {visible.map((r) => (
                    <Table.Row key={r.key} className={r.status === 'differs' || r.status === 'missing' ? `bad-${r.status}` : undefined}>
                      <Table.HeaderCell scope="row" className="lab c1" title={`${r.id} – ${r.partner}`}>{r.id}<div className="sub">{r.partner || '–'}</div></Table.HeaderCell>
                      <Table.Cell>{r.scope === 'header' ? '–' : r.month}</Table.Cell>
                      <Table.Cell className="lineitem">{r.label}<div className="sub">{r.sectionName}</div></Table.Cell>
                      <Table.Cell textAlign="right">{show(r, r.reported)}<div className="sub">{r.source}</div></Table.Cell>
                      <Table.Cell textAlign="right">{show(r, r.fresh)}</Table.Cell>
                      <Table.Cell textAlign="right">{r.diff === null ? '–' : r.diff === 0 ? '0' : signed(r.kind, r.diff, fmt)}</Table.Cell>
                      <Table.Cell textAlign="right">{r.pct === null ? '–' : `${r.pct > 0 ? '+' : ''}${r.pct.toFixed(1)}%`}</Table.Cell>
                      <Table.Cell><Label basic size="small" color={BADGE[r.status][1]}><Icon name={BADGE[r.status][0]} />{STATUS[r.status]}</Label></Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}
          {shown.length > visible.length && (
            <div className="toolbar"><Button type="button" basic onClick={() => setLimit((l) => l + PAGE)}>Show {Math.min(PAGE, shown.length - visible.length)} more</Button></div>
          )}
          <p className="hint">
            <strong>Reported + new data</strong> is each report’s own header fields and monthly figures, plus the new data for later months (marked “New”).
            <strong> All new data</strong> is settlement_report_latest (header) and settlement_report_figure_latest (line items). Difference = all new data − reported; differences within rounding (under 0.5 p, 0.5 of a unit, 0.05 points) count as a match. Header fields are compared as text (extra spaces ignored).
          </p>
        </>
      )}
    </section>
  );
}
